import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { CreateTenantRequest, TenantSummary, UpdateTenantRequest } from '@vce/shared';
import { Button, Dialog, Input, Select } from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';

const EMPTY_TENANT: CreateTenantRequest = {
  name: '',
  accountName: '',
  environment: 'vtexcommercestable',
  appKey: '',
  appToken: '',
  isSandbox: false,
};

export function TenantFormDialog({
  tenant,
  onClose,
  onSaved,
}: {
  tenant?: TenantSummary;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = tenant !== undefined;

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? `Editar ${tenant.name}` : 'Nova conta VTEX'}
      description={
        isEdit
          ? 'Atualize o nome, o status ou rode credenciais novas. Deixe AppKey/AppToken em branco para manter as atuais.'
          : 'As credenciais são cifradas com AES-256-GCM antes de ir ao banco.'
      }
    >
      {isEdit ? (
        <EditTenantFields tenant={tenant} onCancel={onClose} onSaved={onSaved} />
      ) : (
        <CreateTenantFields onCancel={onClose} onSaved={onSaved} />
      )}
    </Dialog>
  );
}

function CreateTenantFields({ onCancel, onSaved }: { onCancel(): void; onSaved(): void }) {
  const [form, setForm] = useState<CreateTenantRequest>(EMPTY_TENANT);
  const nameId = useId();
  const accountId = useId();
  const envId = useId();
  const keyId = useId();
  const tokenId = useId();

  const create = useMutation({
    mutationFn: () => api.tenants.create(form),
    onSuccess: () => {
      toast.success('Conta cadastrada');
      onSaved();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao cadastrar a conta'),
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledField label="Nome de exibição" htmlFor={nameId}>
          <Input
            id={nameId}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
        </LabeledField>
        <LabeledField
          label="accountName da VTEX"
          htmlFor={accountId}
          hint="Só minúsculas, números e hífen."
        >
          <Input
            id={accountId}
            value={form.accountName}
            aria-describedby={`${accountId}-hint`}
            onChange={(event) => setForm({ ...form, accountName: event.target.value })}
            required
            pattern="[a-z0-9-]+"
            title="Apenas minúsculas, números e hífen"
          />
        </LabeledField>
        <LabeledField label="Ambiente" htmlFor={envId}>
          <Select
            id={envId}
            className="w-full"
            value={form.environment}
            onChange={(event) =>
              setForm({
                ...form,
                environment: event.target.value as CreateTenantRequest['environment'],
              })
            }
          >
            <option value="vtexcommercestable">vtexcommercestable</option>
            <option value="vtexcommercebeta">vtexcommercebeta</option>
          </Select>
        </LabeledField>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-primary)]"
              checked={form.isSandbox}
              onChange={(event) => setForm({ ...form, isSandbox: event.target.checked })}
            />
            Conta de sandbox
          </label>
        </div>
        <LabeledField label="X-VTEX-API-AppKey" htmlFor={keyId}>
          <Input
            id={keyId}
            type="password"
            autoComplete="off"
            value={form.appKey}
            onChange={(event) => setForm({ ...form, appKey: event.target.value })}
            required
          />
        </LabeledField>
        <LabeledField label="X-VTEX-API-AppToken" htmlFor={tokenId}>
          <Input
            id={tokenId}
            type="password"
            autoComplete="off"
            value={form.appToken}
            onChange={(event) => setForm({ ...form, appToken: event.target.value })}
            required
          />
        </LabeledField>
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={create.isPending}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={create.isPending}>
          {create.isPending ? 'Cadastrando…' : 'Cadastrar conta'}
        </Button>
      </div>
    </form>
  );
}

function EditTenantFields({
  tenant,
  onCancel,
  onSaved,
}: {
  tenant: TenantSummary;
  onCancel(): void;
  onSaved(): void;
}) {
  const [name, setName] = useState(tenant.name);
  const [isSandbox, setSandbox] = useState(tenant.isSandbox);
  const [isActive, setActive] = useState(tenant.isActive);
  const [appKey, setAppKey] = useState('');
  const [appToken, setAppToken] = useState('');
  const nameId = useId();
  const keyId = useId();
  const tokenId = useId();

  const save = useMutation({
    mutationFn: () => {
      const body: UpdateTenantRequest = { name, isSandbox, isActive };
      if (appKey && appToken) {
        body.appKey = appKey;
        body.appToken = appToken;
      }
      return api.tenants.update(tenant.id, body);
    },
    onSuccess: () => {
      toast.success('Conta atualizada');
      onSaved();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Falha ao atualizar a conta'),
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    save.mutate();
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <LabeledField label="Nome de exibição" htmlFor={nameId}>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} required />
      </LabeledField>
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledField label="Nova AppKey (opcional)" htmlFor={keyId}>
          <Input
            id={keyId}
            type="password"
            autoComplete="off"
            value={appKey}
            onChange={(event) => setAppKey(event.target.value)}
          />
        </LabeledField>
        <LabeledField label="Novo AppToken (opcional)" htmlFor={tokenId}>
          <Input
            id={tokenId}
            type="password"
            autoComplete="off"
            value={appToken}
            onChange={(event) => setAppToken(event.target.value)}
          />
        </LabeledField>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-[var(--color-body)]">Status</legend>
        <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
          <input
            type="checkbox"
            className="size-4 accent-[var(--color-primary)]"
            checked={isSandbox}
            onChange={(event) => setSandbox(event.target.checked)}
          />
          Conta de sandbox
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
          <input
            type="checkbox"
            className="size-4 accent-[var(--color-primary)]"
            checked={isActive}
            onChange={(event) => setActive(event.target.checked)}
          />
          Conta ativa
        </label>
      </fieldset>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={save.isPending}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar conta'}
        </Button>
      </div>
    </form>
  );
}

function LabeledField({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-[var(--color-body)]">
        {label}
      </label>
      {hint ? (
        <p id={`${htmlFor}-hint`} className="mb-1.5 text-[11px] leading-snug text-[var(--color-muted)]">
          {hint}
        </p>
      ) : null}
      {children}
    </div>
  );
}
